import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchDesignByCode, updateDesign, deleteDesign } from "../api/DesignAPIs";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  Search, 
  Package, 
  Edit, 
  Trash2, 
  Copy, 
  Download, 
  X, 
  Save,
  Image as ImageIcon,
  ExternalLink,
  ChevronRight,
  AlertCircle
} from "lucide-react";

const SkeletonLoader = () => {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-10 bg-gray-200 rounded-lg w-64"></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="h-8 bg-gray-200 rounded w-3/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  );
};

const SearchDesignPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchCode, setSearchCode] = useState("");
  const [editingDesign, setEditingDesign] = useState(null);

  const {
    data: designData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["designByCode", searchCode],
    queryFn: () => fetchDesignByCode(searchCode),
    enabled: false,
    staleTime: Infinity,
  });

  const design = designData
    ? {
        id: designData.designId,
        name: designData.name || "Unnamed Product",
        imageUrl_1: designData.imageUrl_1 || "",
        newPrice: designData.priceAfter || 0,
        oldPrice: designData.priceBefore || 0,
        inStock: designData.inStock !== undefined ? designData.inStock : true,
        hasOffer: designData.hasOffer || false,
        material: designData.material || "",
        description: designData.description || "",
        driveLink: designData.driveLink || "",
        code: designData.code || "",
        sales: designData.sales || 0,
      }
    : null;

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchCode.trim()) {
      toast.error("يرجى إدخال كود التصميم");
      return;
    }
    refetch();
  };

  const handleImageChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingDesign((prev) => ({
          ...prev,
          [field]: reader.result.split(",")[1],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingDesign((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateDesign(editingDesign.id, {
        name: editingDesign.name,
        priceBefore: editingDesign.oldPrice,
        priceAfter: editingDesign.newPrice,
        driveLink: editingDesign.driveLink,
        inStock: editingDesign.inStock,
        hasOffer: editingDesign.hasOffer,
        imageBase64_1: editingDesign.imageBase64_1,
        imageBase64_2: editingDesign.imageBase64_2,
      });

      toast.success("تم تحديث التصميم بنجاح!");
      setEditingDesign(null);
      refetch();
    } catch (err) {
      toast.error("فشل في تحديث التصميم");
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("هل أنت متأكد أنك تريد حذف هذا التصميم نهائيًا؟")) {
      return;
    }

    try {
      await deleteDesign(design.id);
      toast.success("تم حذف التصميم بنجاح!");
      setSearchCode("");
      queryClient.invalidateQueries({ queryKey: ["designs"] });
    } catch (err) {
      toast.error("فشل في حذف التصميم");
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            البحث عن تصميم
          </h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة إلى الداشبورد
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 md:p-8 mb-8 border border-white/30">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="أدخل كود التصميم (مثل: G001)"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                className="w-full p-5 pr-14 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white/90"
              />
              <Package className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-5 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg shadow-lg disabled:opacity-70 flex items-center justify-center gap-2"
            >
              <Search size={24} />
              {isLoading ? "جاري البحث..." : "بحث"}
            </button>
          </form>
        </div>

        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8">
            <SkeletonLoader />
          </div>
        ) : error ? (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-8 py-6 rounded-2xl text-center">
            <AlertCircle className="mx-auto mb-3" size={48} />
            <p className="text-xl">حدث خطأ أثناء جلب البيانات. تأكد من الكود وحاول مرة أخرى.</p>
          </div>
        ) : design ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 md:p-8 border border-white/30">
            {/* Design Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl md:text-3xl font-bold text-indigo-600">
                  نتيجة البحث
                </h2>
                <span className="bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 px-4 py-2 rounded-xl font-mono text-lg border border-indigo-200">
                  {design.code}
                </span>
              </div>

              {design.driveLink && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(design.driveLink);
                    toast.success("تم نسخ الرابط بنجاح!");
                  }}
                  className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <Copy size={20} />
                  نسخ الرابط
                </button>
              )}
            </div>

            {/* Design Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Info Section */}
              <div className="space-y-6">
                <div className="bg-white/60 rounded-xl p-6 border border-gray-100">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">معلومات التصميم</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500">الاسم</p>
                      <p className="text-xl font-bold text-gray-800">{design.name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">المادة</p>
                        <p className="text-lg">{design.material || "غير محددة"}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">المبيعات</p>
                        <p className="text-2xl font-bold text-green-600">{design.sales}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">الوصف</p>
                      <p className="text-lg">{design.description || "لا يوجد"}</p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-sm text-gray-500 mb-2">السعر</p>
                      <div className="flex items-center gap-3">
                        {design.oldPrice > design.newPrice && (
                          <span className="line-through text-gray-400 text-xl">{design.oldPrice} ج</span>
                        )}
                        <span className="text-3xl font-bold text-green-600">{design.newPrice} جنيه</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <span className={`px-4 py-2 rounded-xl text-sm font-medium ${
                        design.inStock 
                          ? "bg-green-100 text-green-700 border border-green-200" 
                          : "bg-red-100 text-red-700 border border-red-200"
                      }`}>
                        {design.inStock ? "متوفر في المخزون" : "غير متوفر"}
                      </span>
                      {design.hasOffer && (
                        <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl text-sm font-medium border border-orange-200">
                          عرض خاص
                        </span>
                      )}
                    </div>

                    {design.driveLink && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">رابط الدرايف</p>
                        <a
                          href={design.driveLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          <ExternalLink size={18} />
                          فتح الرابط
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Images Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-800 mb-4">الصور</h3>
                {design.imageUrl_1 ? (
                  <div className="group relative bg-white/60 rounded-xl p-4 border border-gray-200 hover:shadow-xl transition-all">
                    <img
                      src={`${API_BASE_URL_V2}${design.imageUrl_1}`}
                      alt={design.name}
                      className="w-full max-h-96 object-contain rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-4">
                      <button className="bg-white text-gray-800 p-3 rounded-full hover:scale-110 transition">
                        <Download size={24} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-100 rounded-xl p-12 flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-gray-300">
                    <ImageIcon size={48} className="mb-3" />
                    <p>لا توجد صور</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <button
                onClick={() => setEditingDesign(design)}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
              >
                <Edit size={24} />
                تعديل التصميم
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
              >
                <Trash2 size={24} />
                حذف التصميم
              </button>
            </div>
          </div>
        ) : searchCode ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <Package className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-3xl text-gray-700 mb-2">لا يوجد تصميم بهذا الكود</p>
            <p className="text-xl text-gray-500">: {searchCode}</p>
            <p className="text-lg text-gray-500 mt-4">تأكد من الكود وحاول مرة أخرى</p>
          </div>
        ) : null}

        {/* Edit Modal */}
        {editingDesign && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">تعديل التصميم: {editingDesign.code}</h2>
                <button
                  onClick={() => setEditingDesign(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdate} className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">اسم التصميم</label>
                    <input
                      type="text"
                      name="name"
                      value={editingDesign.name}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400"
                      required
                    />
                  </div>

                  {/* Code (Read Only) */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">الكود</label>
                    <input
                      type="text"
                      value={editingDesign.code}
                      readOnly
                      className="w-full p-4 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-600"
                    />
                  </div>

                  {/* Prices */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر قبل الخصم</label>
                    <input
                      type="number"
                      name="oldPrice"
                      value={editingDesign.oldPrice}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">السعر بعد الخصم</label>
                    <input
                      type="number"
                      name="newPrice"
                      value={editingDesign.newPrice}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300"
                      required
                    />
                  </div>

                  {/* Drive Link */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-right font-medium text-gray-700">رابط درايف</label>
                    <input
                      type="text"
                      name="driveLink"
                      value={editingDesign.driveLink}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="inStock"
                        checked={editingDesign.inStock}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <span className="font-medium text-gray-700">في المخزون</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="hasOffer"
                        checked={editingDesign.hasOffer}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
                      />
                      <span className="font-medium text-gray-700">عرض خاص</span>
                    </label>
                  </div>

                  {/* Images */}
                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">صورة 1</label>
                    {editingDesign.imageUrl_1 && (
                      <img 
                        src={`${API_BASE_URL_V2}${editingDesign.imageUrl_1}`} 
                        alt="حالية" 
                        className="h-40 object-contain rounded-xl border-2 border-gray-200 p-2 mb-3" 
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, "imageBase64_1")}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-right font-medium text-gray-700">صورة 2</label>
                    {editingDesign.imageUrl_2 && (
                      <img 
                        src={`${API_BASE_URL_V2}${editingDesign.imageUrl_2}`} 
                        alt="حالية" 
                        className="h-40 object-contain rounded-xl border-2 border-gray-200 p-2 mb-3" 
                      />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, "imageBase64_2")}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl"
                    />
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex justify-center gap-6 mt-10">
                  <button
                    type="button"
                    onClick={() => setEditingDesign(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-8 py-4 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-10 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التعديلات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchDesignPage;